/**
 * Domain config for the comparison-metric feature: which metrics are
 * selectable, their native units, default multiples, and ratio naming.
 * Kept separate from chartConfig.js, which only builds generic Highcharts
 * option objects.
 */

/**
 * Plausible-multiple outlier bounds, `(PE_MIN, PE_MAX]`: at or below zero
 * means the denominator was negative/zero (not a meaningful multiple);
 * above PE_MAX is almost always a near-zero-denominator artifact rather than
 * a genuine valuation signal. The single hard ceiling every metric's own
 * `ratioCeiling` (below) is capped at, and the outlier bound the valuation
 * card's forward-multiple backtest applies (`valuationRegression.js`) —
 * defined here, not there, since `ratioCeiling` needs it too and a metric's
 * config shouldn't depend on one specific card's utils module.
 */
export const PE_MIN = 0;
export const PE_MAX = 150;

export const METRIC_OPTIONS = [
  // Labeled "Earnings" (not "EPS") because the same underlying metric is
  // shown per-share (earnings per share) or as a total (net income)
  // depending on the display-scale toggle — it isn't always per-share.
  // Internal key stays `eps` (the backend contract's metric key).
  { value: 'eps', label: 'Earnings' },
  // Displayed as "Sales" (not "Revenue") so it's consistent with the ratio
  // name P/S — the two terms are interchangeable for the top line, and the
  // ratio is universally "P/S", never "P/Rev". Internal key stays `revenue`.
  { value: 'revenue', label: 'Sales' },
  { value: 'ebitda', label: 'EBITDA' },
  { value: 'ebit', label: 'EBIT' },
  { value: 'netIncome', label: 'Net Income' },
  { value: 'fcf', label: 'FCF' },
  { value: 'ocf', label: 'OCF' },
  { value: 'dividends', label: 'Dividends' },
];

export const METRIC_LABELS = METRIC_OPTIONS.reduce(
  (acc, { value, label }) => ({ ...acc, [value]: label }), {}
);

/**
 * The main chart's "{Metric} × Multiple" fair-value comparison line uses one
 * fixed multiple against a single metric, so it doesn't get the ratio
 * chart's per-point ceiling/outlier handling — per product decision,
 * dividends is excluded here (still fully available on the ratio/yield
 * chart's multi-select and the valuation-regression card, both of which
 * already handle its near-zero-denominator behavior).
 */
export const MAIN_CHART_METRIC_OPTIONS = METRIC_OPTIONS.filter(({ value }) => value !== 'dividends');

/**
 * Metrics the "Valuation vs. Subsequent Returns" card plots as a YIELD
 * (metric-per-share ÷ price, a small fraction) rather than as a forward
 * MULTIPLE (price ÷ metric-per-share, e.g. a P/E). Dividends is the only
 * one: a "P/Div" ratio is an awkward, rarely-quoted figure (and its
 * denominator — dividends per share — is small enough that the ratio
 * balloons into the hundreds), whereas dividend YIELD is the standard,
 * intuitive way the market frames dividend valuation. Every other metric
 * stays a multiple, so this is a per-metric flag, not a mode switch.
 */
export const METRIC_AS_YIELD = {
  dividends: true,
};

/** True when this card should plot `metric` as a yield rather than a
 * forward multiple — see `METRIC_AS_YIELD`. */
export const isYieldMetric = (metric) => Boolean(METRIC_AS_YIELD[metric]);

/** Whether FMP natively reports this metric per-share (only EPS) or as a
 * total company dollar figure (everything else). */
export const METRIC_NATIVE_BASIS = {
  eps: 'perShare',
  revenue: 'total',
  ebitda: 'total',
  ebit: 'total',
  netIncome: 'total',
  fcf: 'total',
  ocf: 'total',
  dividends: 'perShare',
};

const METRIC_ABBREV = {
  eps: 'E', revenue: 'S', ebitda: 'EBITDA', ebit: 'EBIT',
  netIncome: 'NI', fcf: 'FCF', ocf: 'OCF', dividends: 'Div',
};

/** Standard finance ratio name for a metric/anchor pair, e.g. "P/E", "EV/FCF". */
export const ratioName = (metric, valueBasis) => (
  (valueBasis === 'marketCap' ? 'P/' : 'EV/') + METRIC_ABBREV[metric]
);

const METRIC_YIELD_LABEL = {
  eps: 'Earnings', revenue: 'Sales', ebitda: 'EBITDA', ebit: 'EBIT',
  netIncome: 'Net Income', fcf: 'FCF', ocf: 'OCF', dividends: 'Dividend',
};

/** Yield-style name for a metric/anchor pair — the reciprocal framing of
 * ratioName, e.g. "Earnings Yield" (marketCap) or "FCF/EV Yield" (enterpriseValue). */
export const yieldName = (metric, valueBasis) => (
  valueBasis === 'marketCap' ? `${METRIC_YIELD_LABEL[metric]} Yield` : `${METRIC_ABBREV[metric]}/EV Yield`
);

/** Label for the metric's raw value at the given display scale — Earnings
 * (per share) / Net Income (total) swap names entirely (that's how finance
 * refers to the same quantity in different units); everything else just gets
 * a "/Share" suffix when shown per-share. */
export const metricDisplayLabel = (metric, displayScale) => {
  if (metric === 'eps') return displayScale === 'total' ? 'Net Income' : 'Earnings';
  const label = METRIC_LABELS[metric];
  return displayScale === 'perShare' ? `${label}/Share` : label;
};

// Sensible starting multiples per metric/anchor, loaded when either changes.
// Approximate industry-typical ballparks — still freely editable afterward.
const DEFAULT_MULTIPLES = {
  eps: { marketCap: 15, enterpriseValue: 15 },
  revenue: { marketCap: 3, enterpriseValue: 3 },
  ebitda: { marketCap: 12, enterpriseValue: 10 },
  ebit: { marketCap: 14, enterpriseValue: 12 },
  netIncome: { marketCap: 15, enterpriseValue: 15 },
  fcf: { marketCap: 20, enterpriseValue: 18 },
  ocf: { marketCap: 14, enterpriseValue: 12 },
  dividends: { marketCap: 40, enterpriseValue: 40 },
};

export const defaultMultiple = (metric, valueBasis) => (
  DEFAULT_MULTIPLES[metric]?.[valueBasis] ?? 15
);

/**
 * Validity ceiling for a ratio/multiple, in `ratio` mode only. A metric
 * whose denominator approaches zero can produce an arbitrarily large
 * multiple that is outside this application's stable-fundamental valuation
 * domain. `buildRatioSeries` emits those points as gaps; overlay statistics
 * and the defensive axis guard reuse the same ceiling so all ratio-chart
 * consumers agree on the supported range. Scaled from each metric's own
 * default multiple (a P/S ceiling is lower than a P/E ceiling), but never
 * above `PE_MAX`. Yield mode has no denominator singularity in this
 * direction, so it has no ceiling.
 */
export const ratioCeiling = (metric, valueBasis, ratioMode) => (
  ratioMode === 'yield' ? null : Math.min(10 * defaultMultiple(metric, valueBasis), PE_MAX)
);

// One fixed, CVD-safe categorical hue per metric, in METRIC_OPTIONS' fixed
// order, so a metric's ratio line, its avg/±1σ overlay, and its checklist
// swatch always share the same color and that color never changes when
// other metrics are ticked/unticked.
// EBIT (4th slot) was the amber/yellow #eda100, which now clashes with the
// main chart's yellow dividend fair-value overlay — swap it to the red
// #e34948 that dividends held (dividends never draws with its palette color;
// its main-chart overlay is hardcoded yellow in useStockData.js), so no two
// ratio-chart lines share a hue.
const METRIC_COLOR_PALETTE = [
  '#2a78d6', '#eb6834', '#1baf7a', '#e34948', '#e87ba4', '#008300', '#4a3aa7', '#eda100',
];

export const METRIC_COLORS = METRIC_OPTIONS.reduce(
  (acc, { value }, i) => ({ ...acc, [value]: METRIC_COLOR_PALETTE[i] }), {}
);
